import type { ColumnDef, ReportFormat, ReportScope, ReportSectionHint, ReportTemplateModel } from '@/gql/graphql'
// Reused, not restated: a second wording for the same rule is how two surfaces drift apart.
// report-filters imports nothing from here, so this direction introduces no cycle.
import { DATE_RANGE_INVALID_MESSAGE } from '@/components/reports/report-filters'

// ── sectionHint grouping (T03) ──────────────────────────────────────────────
// Real grouping key per the T17 handover — NOT `category`, which is a looser legacy
// label (e.g. debenture-holders/shareholders-register/share-certificates are
// category: 'shares' but sectionHint: OWNERSHIP).

export const SECTION_LABELS: Record<ReportSectionHint, string> = {
  PORTFOLIO_SUMMARY: 'Portfolio Summary',
  COMPANY: 'Company',
  GOVERNANCE: 'Governance',
  OWNERSHIP: 'Ownership',
  COMPLIANCE_DOCUMENTS: 'Compliance & Documents',
  AUDIT_HISTORY: 'Audit & Change',
}

// T01 (ENT-466-CARDPARITY) — editorial UI copy, same category as WIZARD_STEP_CAPTIONS
// (ReportsPage.tsx) which is already hardcoded, NOT derived from any API field.
export const SECTION_SUBTITLES: Record<ReportSectionHint, string> = {
  PORTFOLIO_SUMMARY: 'High-level rollups across the whole portfolio.',
  COMPANY: 'Identity, jurisdiction, status and core company facts.',
  GOVERNANCE: 'Directors, officers, committees and other appointments.',
  OWNERSHIP: 'Shareholders, debenture holders and ownership structure.',
  COMPLIANCE_DOCUMENTS: 'Constitution documents, registrations and filings.',
  AUDIT_HISTORY: 'Changes and activity across the portfolio over time.',
}

export const SECTION_ORDER: ReportSectionHint[] = [
  'PORTFOLIO_SUMMARY',
  'COMPANY',
  'GOVERNANCE',
  'OWNERSHIP',
  'COMPLIANCE_DOCUMENTS',
  'AUDIT_HISTORY',
]

// ── async export format resolution (T04/T06) ────────────────────────────────
// `requestReportExport` accepts CSV/XLSX only — HTML is sync-preview-only
// (generateReport) and is rejected server-side with REPORT_FORMAT_NOT_SUPPORTED.
// `reportTemplates.supportedFormats` still legitimately lists HTML (it describes
// generateReport's capability), so the async picker must intersect, never pass
// supportedFormats through directly.

const ASYNC_EXPORT_FORMATS: ReadonlySet<ReportFormat> = new Set(['CSV', 'XLSX'])

export function availableExportFormats(supportedFormats: ReportFormat[]): ReportFormat[] {
  return supportedFormats.filter((f) => ASYNC_EXPORT_FORMATS.has(f))
}

// ── column selection helpers (T05) ──────────────────────────────────────────

/**
 * Default selection derived from `columnManifest`'s `defaultOn` entries — called on
 * template selection / template change so the picker re-initializes rather than
 * carrying over a stale, template-specific selection.
 */
export function defaultSelectedColumns(columnManifest: ColumnDef[]): string[] {
  return columnManifest.filter((c) => c.defaultOn).map((c) => c.key)
}

/**
 * Strips any selected key not present in the current columnManifest — defense in
 * depth against a stale selection (e.g. after switching templates) reaching a submit
 * handler. The server's REPORT_UNKNOWN_COLUMN error is the real gate; this guards the
 * picker's own bug surface, not the contract.
 */
export function validSelectedColumns(columnManifest: ColumnDef[], selected: string[]): string[] {
  const validKeys = new Set(columnManifest.map((c) => c.key))
  return selected.filter((k) => validKeys.has(k))
}

/**
 * EW-T15 (FR-9.8): the selected keys in MANIFEST order, whatever order the user clicked them.
 *
 * The picker used to append on toggle, so the payload carried click-order — and the backend
 * discarded it anyway via `template.columns.filter(...)`. Sending an order the server ignores
 * advertises a capability the product has explicitly DECLINED (Q2: no user-controlled column
 * order). Ordering here by the manifest makes the wire say what is actually true.
 *
 * Composes with validSelectedColumns rather than replacing it: stale keys are still stripped.
 */
export function manifestOrderedColumns(columnManifest: ColumnDef[], selected: string[]): string[] {
  const chosen = new Set(validSelectedColumns(columnManifest, selected))
  return columnManifest.filter((c) => chosen.has(c.key)).map((c) => c.key)
}

// ── configure-step state, lifted to ReportsPage (F-S058-EDITCFG) ────────────
//
// ReportConfigStep used to own scope/entityIds/filterValues/selectedColumns/format/customTitle
// as local useState, reset on template change via a useEffect keyed on template.id. That worked
// for a genuinely NEW template, but ReportsPage only renders ReportConfigStep while
// `step === 'params'` — going to 'preview' (Review export) and back (Edit configuration)
// unmounts and remounts it, and remounting re-runs every useState initializer regardless of the
// template-change effect. The fix is to lift this state to ReportsPage (which persists across the
// step transition) and make ReportConfigStep a controlled component. `defaultReportConfigState`
// replaces the old effect: ReportsPage calls it ONLY when a genuinely new template is selected,
// never on Edit configuration.

export interface ReportConfigState {
  scope: ReportScope
  entityIds: string[]
  filterValues: Record<string, string>
  selectedColumns: string[]
  format: ReportFormat | null
  customTitle: string
}

export function defaultReportConfigState(template: ReportTemplateModel): ReportConfigState {
  return {
    scope: template.supportedScopes[0],
    entityIds: [],
    filterValues: {},
    selectedColumns: defaultSelectedColumns(template.columnManifest),
    format: availableExportFormats(template.supportedFormats)[0] ?? null,
    customTitle: '',
  }
}

// ── report run error copy (T02, ENT-466-RECENT) ─────────────────────────────
//
// Both raw fields on a FAILED ReportRun are machine codes, not human-readable —
// confirmed live: errorType="REPORT_GENERATION_FAILED", errorMessage="Activity task
// failed". Neither is fit to show a user directly. This maps known codes to human
// copy; the raw code is still shown as small, labelled secondary text (never hidden
// — it's the real diagnostic value for support), and any unmapped code falls back to
// ReportExportRun.tsx's existing generic message rather than a blank or the raw code.

const REPORT_ERROR_COPY: Record<string, string> = {
  REPORT_GENERATION_FAILED: 'Something went wrong while generating this report.',
  REPORT_EXPORT_DISABLED: 'Exporting this report is currently disabled.',
  // entity-backend !1577: a run whose workflow never started (EM_10480), and a run the
  // stranded-run sweep failed because its workflow was gone (EM_10481). Both are safe to rerun.
  EM_REPORT_WORKFLOW_START_FAILED: 'This report could not be started. Run it again.',
  EM_REPORT_RUN_WORKFLOW_LOST: 'This report stopped before it finished. Run it again.',
}

const GENERIC_REPORT_ERROR_COPY = 'An unexpected error occurred while generating this export.'

export function mapReportErrorType(errorType: string | null | undefined): string {
  if (!errorType) return GENERIC_REPORT_ERROR_COPY
  return REPORT_ERROR_COPY[errorType] ?? GENERIC_REPORT_ERROR_COPY
}

// ── relative time + staleness (T03, ENT-466-RECENT) ─────────────────────────
//
// A 4th local relative-time implementation, matching the existing pattern in
// src/components/ask/canvas/AgentSessionList.tsx (12m / 3h / 2d / absolute date) —
// deliberately NOT extracted into a shared lib, consistent with the 3 existing
// ad-hoc implementations already in the codebase (see PRD Scope decisions).
// `now` is an injectable param (defaults to Date.now()) so tests can assert both
// sides of the staleness boundary without fake timers.

export function relativeTime(iso: string, now: number = Date.now()): string {
  const then = Date.parse(iso)
  if (!Number.isFinite(then)) return ''
  const mins = Math.floor((now - then) / 60_000)
  if (mins < 1) return 'now'
  if (mins < 60) return `${mins}m`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h`
  const days = Math.floor(hrs / 24)
  if (days < 7) return `${days}d`
  return new Date(then).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

// Threshold grounded in observed dev data (see PRD Scope decisions): healthy runs
// complete in ~2.5-5s; stuck runs on dev have sat for hours-to-days. 2 minutes is
// comfortably past any healthy run's completion time. Do not change without
// equivalent evidence.
export const STALE_RUN_THRESHOLD_MS = 2 * 60 * 1000

/** True once a non-terminal (PENDING/RUNNING) run has been going longer than the staleness threshold. */
export function isStaleRun(startedAtOrCreatedAt: string, now: number = Date.now()): boolean {
  const then = Date.parse(startedAtOrCreatedAt)
  if (!Number.isFinite(then)) return false
  return now - then > STALE_RUN_THRESHOLD_MS
}

// ── SELECTED-scope requirement (EW-T09/T11, FR-7.2 + FR-10.2) ──────────────
//
// ONE string, shared by the configure step's inline reason and the save guard. FR-10.2 chose
// PREVENTING an unexportable save over inventing a "draft" concept, and requires it to give
// "the same inline reason as FR-7.2" — so this must not be duplicated into a second wording
// that can drift.

export const SELECTED_SCOPE_REQUIRES_ENTITIES = 'Select at least one entity to export this report.'
export const NO_COLUMNS_SELECTED = 'Select at least one column to export this report.'

/** True when the configuration cannot be exported because SELECTED scope has no entities. */
export function isSelectedScopeWithoutEntities(scope: ReportScope, entityIds: string[]): boolean {
  return scope === 'SELECTED' && entityIds.length === 0
}

// ── one predicate for "this configuration is not valid to PERSIST" ─────────
//
// FR-10.2 says prevent saving a configuration that CANNOT BE EXPORTED. The first implementation
// used the scope-only check, which is STRICTLY WEAKER than the export guard: after "Clear" (zero
// columns) or with a reversed date range, Save was enabled while Review export was disabled, so
// the dialog could persist a definition the same screen refused to export — which then fails
// backend validation on re-run. Found by cross-vendor review of MR !300 (codex, P1).
//
// AC-18 still PASSED, because its example exercised only the scope path. An acceptance criterion
// can pass while its functional requirement is only partly met, when the AC's example is narrower
// than the FR's words. Hence ONE predicate, derived once and consumed by both guards, rather than
// two conditions that agreed by coincidence.
//
// `format` is deliberately NOT here: SaveReportDefinitionInput persists
// {templateId, scope, columns, filters, name} and has NO format field — a saved definition is the
// preset only, and format is chosen fresh at re-run time. Gating Save on format would block a
// perfectly valid save.

export interface ReportSaveValidityInput {
  scope: ReportScope
  entityIds: string[]
  /** Count AFTER validSelectedColumns() — stale keys must not count towards validity. */
  validColumnCount: number
  dateRangeInvalid: boolean
}

/**
 * The reason this configuration cannot be persisted, or `null` if it can.
 *
 * Returning the REASON rather than a boolean is what keeps the disabled state and the explanation
 * in sync — a disabled control whose tooltip says nothing is its own defect.
 */
export function reportSaveBlockReason(input: ReportSaveValidityInput): string | null {
  if (isSelectedScopeWithoutEntities(input.scope, input.entityIds)) {
    return SELECTED_SCOPE_REQUIRES_ENTITIES
  }
  if (input.validColumnCount === 0) return NO_COLUMNS_SELECTED
  if (input.dateRangeInvalid) return DATE_RANGE_INVALID_MESSAGE
  return null
}

// ── elapsed time for an in-flight run (EW-T03, FR-4.1) ─────────────────────
//
// Elapsed time is the ONE piece of run progress that is real: it is measured from a
// timestamp the server actually gave us. Percentages, phases and ETAs have no backend
// support and are explicit anti-requirements (PRD §7) — do not add them here, and do not
// infer one from elapsed time.
//
// `now` is injectable (as with relativeTime/isStaleRun above) so tests can assert exact
// output without fake timers.

/** Compact elapsed duration: "0s", "45s", "1m 30s", "2h 5m". Never negative. */
export function formatElapsed(startIso: string, now: number = Date.now()): string {
  const then = Date.parse(startIso)
  if (!Number.isFinite(then)) return ''
  const totalSec = Math.max(0, Math.floor((now - then) / 1000))
  const hrs = Math.floor(totalSec / 3600)
  const mins = Math.floor((totalSec % 3600) / 60)
  const secs = totalSec % 60
  if (hrs > 0) return `${hrs}h ${mins}m`
  if (mins > 0) return `${mins}m ${secs}s`
  return `${secs}s`
}

// ── scope vocabulary — SINGLE SOURCE (FR-5.1, EW-T00) ──────────────────────
//
// One vocabulary for the ReportScope enum, everywhere. This used to ship twice: the
// configure step had SCOPE_COPY ("All active entities" / "Selected entities") while the
// catalogue card had its own reportScopeLabel() returning "Portfolio" / "Single entity".
// "Single entity" was also factually wrong — SELECTED carries entityIds: string[] and its
// own caption reads "Choose one or more entities".
//
// SCOPE_COPY is now owned here and consumed by BOTH surfaces. Do not reintroduce a second
// mapping in a component.

export const SCOPE_COPY: Record<ReportScope, { label: string; caption: string }> = {
  ALL_ACTIVE: { label: 'All active entities', caption: 'Across the organization' },
  SELECTED: { label: 'Selected entities', caption: 'Choose one or more entities' },
}

/** Dual-scope templates support both — neither single label alone is truthful. */
const DUAL_SCOPE_LABEL = 'All active or selected entities'

// ── catalogue card meta row (T00, ENT-466-CARDPARITY) ───────────────────────

/**
 * Scope label for a template card, derived from SCOPE_COPY (FR-5.2). Never returns a
 * blank label: an empty/unknown scope list falls back to the dual-scope wording, which is
 * the only one true of an unconstrained template.
 *
 * NOTE: the FR-5.3 ban on "Portfolio" is scoped to SCOPE LABELS only. The section heading
 * SECTION_LABELS.PORTFOLIO_SUMMARY = 'Portfolio Summary' is a *section* name, is unaffected,
 * and MUST remain (AC-9 is explicit about this).
 */
export function reportScopeLabel(supportedScopes: ReportScope[]): string {
  const hasAll = supportedScopes.includes('ALL_ACTIVE')
  const hasSelected = supportedScopes.includes('SELECTED')
  if (hasAll && hasSelected) return DUAL_SCOPE_LABEL
  if (hasAll) return SCOPE_COPY.ALL_ACTIVE.label
  if (hasSelected) return SCOPE_COPY.SELECTED.label
  return DUAL_SCOPE_LABEL
}

const CATEGORY_LABELS: Record<string, string> = {
  company: 'Company',
  appointments: 'Appointments',
  shares: 'Shares',
  data_library: 'Data Library',
}

/**
 * `category` is a free-form GraphQL `String!`, not a closed enum (unlike `sectionHint`) — a
 * fallback-free map would silently blank on any BE-added value this map hasn't seen yet.
 * Title-cases the raw slug as a safe fallback, never a blank chip or a crash.
 */
export function reportCategoryLabel(category: string): string {
  return CATEGORY_LABELS[category] ?? category
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase())
}
